import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { FIREBASE_DB } from '../firebase.config';
import { Clock, Pin, Calendar, AlertCircle, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { format, formatDistanceToNow, isFuture, isPast, isToday } from 'date-fns';
import { Link } from 'react-router-dom';

const EmployeeBulletinBoard = () => {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const maxItems = 5;

  useEffect(() => {
    const fetchBulletins = async () => {
      try {
        setLoading(true);
        const now = new Date();
        
        let bulletinsQuery = query(
          collection(FIREBASE_DB, 'bulletins'),
          where('isActive', '==', true),
          where('isPublic', '==', false), // Only employee-specific announcements
          orderBy('isPinned', 'desc'),
          orderBy('createdAt', 'desc'),
          limit(maxItems)
        );
        
        const querySnapshot = await getDocs(bulletinsQuery);
        const bulletinsList = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            expiresAt: doc.data().expiresAt?.toDate()
          }))
          .filter(bulletin => !bulletin.expiresAt || bulletin.expiresAt > now);
        
        setBulletins(bulletinsList);
      } catch (error) {
        console.error('Error fetching employee bulletins:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBulletins();
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    return format(new Date(date), 'MMM d, yyyy');
  };

  const getBulletinStatusBadge = (bulletin) => {
    if (bulletin.isPinned) {
      return {
        icon: <Pin size={14} className="mr-1" />,
        text: 'Pinned',
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-800'
      };
    }
    
    if (bulletin.expiresAt) {
      if (isToday(new Date(bulletin.expiresAt))) {
        return {
          icon: <AlertCircle size={14} className="mr-1" />,
          text: 'Expires Today',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800'
        };
      }
      if (isFuture(new Date(bulletin.expiresAt))) {
        const daysLeft = Math.ceil((new Date(bulletin.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
        if (daysLeft <= 3) {
          return {
            icon: <Clock size={14} className="mr-1" />,
            text: `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`,
            bgColor: 'bg-yellow-100',
            textColor: 'text-yellow-800'
          };
        }
      }
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded-lg w-1/3 mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-100 rounded-lg p-4">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (bulletins.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Latest Announcements</h2>
          <Link
            to="/employee/announcements"
            className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-dark"
          >
            View all announcements
            <ArrowRight size={16} className="ml-1" />
          </Link>
        </div>
      </div>

      {/* Announcements List */}
      <div className="divide-y divide-gray-100">
        {bulletins.map(bulletin => {
          const statusBadge = getBulletinStatusBadge(bulletin);
          
          return (
            <div 
              key={bulletin.id} 
              className={`p-6 transition-colors ${
                expandedId === bulletin.id ? 'bg-gray-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 
                      className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-primary transition-colors"
                      onClick={() => setExpandedId(expandedId === bulletin.id ? null : bulletin.id)}
                    >
                      {bulletin.title}
                    </h3>
                    {bulletin.isPinned && (
                      <Pin size={16} className="text-amber-500 ml-2 flex-shrink-0" />
                    )}
                  </div>
                  
                  <div className="mt-2">
                    <p className={`text-gray-600 leading-relaxed ${
                      expandedId === bulletin.id ? '' : 'line-clamp-2'
                    }`}>
                      {bulletin.content}
                    </p>
                    {bulletin.content.length > 120 && (
                      <button 
                        onClick={() => setExpandedId(expandedId === bulletin.id ? null : bulletin.id)} 
                        className="text-sm text-primary hover:text-primary-dark mt-2 flex items-center font-medium"
                      >
                        {expandedId === bulletin.id ? (
                          <>
                            Show less <ChevronUp size={16} className="ml-1" />
                          </>
                        ) : (
                          <>
                            Read more <ChevronDown size={16} className="ml-1" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                      {bulletin.category || 'General'}
                    </span>
                    
                    {statusBadge && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full ${statusBadge.bgColor} ${statusBadge.textColor} text-sm font-medium`}>
                        {statusBadge.icon}
                        {statusBadge.text}
                      </span>
                    )}
                    
                    <span className="inline-flex items-center text-sm text-gray-500">
                      <Calendar size={14} className="mr-1" />
                      {formatDistanceToNow(new Date(bulletin.createdAt), { addSuffix: true })}
                    </span>
                    
                    {bulletin.expiresAt && (
                      <span className="inline-flex items-center text-sm text-gray-500">
                        <Clock size={14} className="mr-1" />
                        Expires: {formatDate(bulletin.expiresAt)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmployeeBulletinBoard; 